import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PostModal = ({ isOpen, post, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    content: '',
  });

  const [file, setFile] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (post) {
      setFormData({
        content: post?.content || '',
      });
    } else {
      setFormData({
        content: '',
      });
    }
    setFile(null);
    setRemoveExistingImage(false);
    setFileInputKey(0);
    setErrors({});
    setIsSaving(false);
  }, [post, isOpen]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next = {};
    if (!post && !file) next.file = '이미지 파일을 업로드해주세요';
    if (post && removeExistingImage && !file) next.file = '이미지를 제거하려면 새 이미지 파일을 선택해주세요';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    try {
      setIsSaving(true);
      await onSave?.({
        content: formData?.content,
        file,
        removeImage: removeExistingImage,
      });
    } catch (err) {
      window.alert(err?.message || '저장에 실패했습니다.');
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card shadow-lg">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">{post ? '게시물 수정' : '게시물 등록'}</h2>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
            aria-label="닫기"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">내용</label>
            <textarea
              name="content"
              value={formData?.content}
              onChange={handleChange}
              placeholder="작업 내용/주의사항/결과 등을 입력하세요"
              rows={5}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="mb-3 text-sm font-semibold text-foreground">이미지</p>

            {(previewUrl || (!removeExistingImage && post?.image_url)) && (
              <div className="mb-4">
                <img
                  src={previewUrl || post?.image_url}
                  alt="미리보기"
                  className="h-56 w-full rounded-md object-contain bg-background"
                />
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">이미지 파일 업로드</label>
              <input
                key={fileInputKey}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setFile(e?.target?.files?.[0] || null);
                  setRemoveExistingImage(false);
                  if (errors?.file) {
                    setErrors((prev) => ({ ...prev, file: '' }));
                  }
                }}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-muted file:px-4 file:py-2 file:text-sm file:font-semibold file:text-foreground hover:file:bg-muted/80"
              />
              {errors?.file && (
                <p className="mt-2 text-xs text-destructive">{errors?.file}</p>
              )}

              {post?.image_url && !file && !removeExistingImage && (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    현재 이미지: <span className="break-all">{post?.image_url}</span>
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRemoveExistingImage(true);
                      setFile(null);
                      setFileInputKey((k) => k + 1);
                    }}
                  >
                    제거
                  </Button>
                </div>
              )}

              {removeExistingImage && !file && (
                <div className="mt-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">현재 이미지를 제거합니다.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRemoveExistingImage(false);
                      setFileInputKey((k) => k + 1);
                    }}
                  >
                    취소
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              취소
            </Button>
            <Button type="submit" variant="default" iconName="Save" iconPosition="left" loading={isSaving}>
              {post ? '수정 완료' : '등록'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostModal;
